import { useEffect, useRef, useState } from 'react';
import { getFullscreenElement } from '../full-screen/full-screen.hook';
import {
  clearBlur,
  initMobileScreenshotGuards,
  notifyPageConcealed,
  applyBlur,
  suppressViewportBlur,
} from './invigilate-blur';
import { useWindow } from '../util/window-helper';

export const useWindowChange = () => {
  const [windowMoveCount, setWindowMoveCount] = useState(0);
  const [fullScreenCloseCount, setFullScreenCloseCount] = useState(0);
  const isHiddenRef = useRef(false);
  /** Skip blur until the test UI has been focused once (mobile often starts unfocused). */
  const hasBeenActiveRef = useRef(false);
  const pollIdRef = useRef<number | null>(null);
  const { isMobile } = useWindow();

  useEffect(() => {
    const cleanupScreenshotGuards = initMobileScreenshotGuards(isMobile);

    const handleResize = () => {
      const leftPosition = window.screenLeft || window.screenX;
      console.log('Window left position:', leftPosition);
    };

    const handleFullscreenChange = () => {
      // Fullscreen toggles resize the viewport; don't treat that as a screenshot.
      suppressViewportBlur(1000);

      if (getFullscreenElement() === null) {
        console.log('Exited fullscreen');
        notifyPageConcealed();
        setFullScreenCloseCount((preState) => preState + 1);
      } else {
        console.log('Entered fullscreen');
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        console.log('Escape key pressed');
      }
    };

    const markActive = () => {
      hasBeenActiveRef.current = true;
      isHiddenRef.current = false;
    };

    const handlePageHidden = () => {
      if (!hasBeenActiveRef.current) {
        return;
      }
      isHiddenRef.current = true;
      // Hard conceal (tab/app switch) — always re-blur until tap.
      notifyPageConcealed();
    };

    const handleFocusLost = () => {
      if (!hasBeenActiveRef.current) {
        return;
      }
      isHiddenRef.current = true;
      // Soft conceal (common after exiting fullscreen). Do not reset a tap-dismiss.
      applyBlur();
    };

    const handlePageVisible = () => {
      markActive();
      // Keep blur on return — user dismisses via "Tap to view".
      applyBlur();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        handlePageHidden();
      } else {
        handlePageVisible();
      }
    };

    const handlePageFreeze = () => {
      if (!hasBeenActiveRef.current) {
        return;
      }
      notifyPageConcealed();
    };

    const handleWindowFocus = () => {
      markActive();
    };

    pollIdRef.current = window.setInterval(() => {
      const isHidden = document.visibilityState === 'hidden';
      const hasFocus = document.hasFocus();

      if (!isHidden && hasFocus) {
        markActive();
        return;
      }

      // Do not blur on initial load when the document is not focused yet.
      if (!hasBeenActiveRef.current) {
        return;
      }

      if (isHidden) {
        isHiddenRef.current = true;
        notifyPageConcealed();
        return;
      }

      // Focus-only loss: re-apply blur only if user has not dismissed.
      isHiddenRef.current = true;
      applyBlur();
    }, 200);

    window.addEventListener('resize', handleResize);
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('focus', handleWindowFocus);
    window.addEventListener('blur', handleFocusLost);
    window.addEventListener('pagehide', handlePageHidden);
    document.addEventListener('freeze', handlePageFreeze);
    document.addEventListener('visibilitychange', handleVisibilityChange, {
      capture: true,
    });

    if (document.visibilityState === 'visible' && document.hasFocus()) {
      markActive();
    }

    return () => {
      cleanupScreenshotGuards();
      if (pollIdRef.current != null) {
        window.clearInterval(pollIdRef.current);
      }
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener(
        'webkitfullscreenchange',
        handleFullscreenChange,
      );
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('focus', handleWindowFocus);
      window.removeEventListener('blur', handleFocusLost);
      window.removeEventListener('pagehide', handlePageHidden);
      document.removeEventListener('freeze', handlePageFreeze);
      document.removeEventListener('visibilitychange', handleVisibilityChange, {
        capture: true,
      });
      clearBlur();
    };
  }, [isMobile]);

  return { windowMoveCount, fullScreenCloseCount };
};
